import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { QuoteStatus } from 'src/app/models/quote/quote-status';
import { RecommendationStatus } from 'src/app/models/recommendation/recommendation-status';
import { RecommendationUserDefinedStatus } from 'src/app/models/recommendation/recommendation-user-defined-status';
import { PostSearch } from 'src/app/models/search/post-search';
import { LoadingControllerWrapperService } from 'src/app/services/loading-controller-wrapper/loading-controller-wrapper.service';
import { ToastControllerWrapperService } from 'src/app/services/toast-controller-wrapper/toast-controller-wrapper.service';
import { UserService } from 'src/app/services/user/user.service';
import { Geolocation } from '@capacitor/geolocation';
import { PostFieldFromGpsGet } from 'src/app/models/search/post-field-from-gps-get';
import { CapacitorBarcodeScanner, CapacitorBarcodeScannerTypeHint, CapacitorBarcodeScannerCameraDirection } from '@capacitor/barcode-scanner';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { forkJoin } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { SearchResults } from 'src/app/models/search/search-results';
import { QuoteUserDefinedStatus } from 'src/app/models/quote/quote-user-defined-status';

@Component({
  selector: 'app-search',
  templateUrl: './search.page.html',
  styleUrls: ['./search.page.scss', '../../../shared-styles/barcode-scanner.scss'],
})
export class SearchPage implements OnInit {

  title: string = 'Search';
  recommendationStatuses: RecommendationStatus[] = [];
  recommendationUserDefinedStatus: RecommendationUserDefinedStatus = <RecommendationUserDefinedStatus>{ description: 'Custom Status' };
  quoteUserDefinedStatus: QuoteUserDefinedStatus = <QuoteUserDefinedStatus>{ description: 'Custom Status' };
  quoteStatuses: QuoteStatus[] = [];
  postSearch: PostSearch = <PostSearch>{};
  isMobilePlatform: boolean;
  scanActive: boolean = false;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private toastWrapper: ToastControllerWrapperService,
    private loadingWrapper: LoadingControllerWrapperService,
    private userService: UserService,
    private userData: UserDataService
  ) { }

  async ngOnInit() {
    this.isMobilePlatform = this.userData.isMobilePlatform();
    this.postSearch = <PostSearch>{};

    await this.loadingWrapper.presentLoading('');

    forkJoin<[RecommendationStatus[], QuoteStatus[], RecommendationUserDefinedStatus, QuoteUserDefinedStatus]>([
      this.route.snapshot.data.recommendationStatus,
      this.route.snapshot.data.quoteStatus,
      this.route.snapshot.data.recommendationUserDefinedStatus,
      this.route.snapshot.data.quoteUserDefinedStatus
    ])
      .pipe(finalize(async () => await this.loadingWrapper.dismissLoading()))
      .subscribe(([recommendationStatus, quoteStatus, recommendationUserDefinedStatus, quoteUserDefinedStatus]: [RecommendationStatus[], QuoteStatus[], RecommendationUserDefinedStatus, QuoteUserDefinedStatus]) => {
        this.recommendationStatuses = recommendationStatus,
          this.quoteStatuses = quoteStatus,
          this.recommendationUserDefinedStatus = recommendationUserDefinedStatus,
          this.quoteUserDefinedStatus = quoteUserDefinedStatus
      });
  }

  ionViewWillEnter() {
    this.titleService.setTitle(this.title);
  }

  async submitSearch() {

    await this.toastWrapper.dismissToast();
    await this.loadingWrapper.presentLoading('');

    this.userService.search(this.postSearch)
      .pipe(finalize(async () => {
        await this.loadingWrapper.dismissLoading();
      }))
      .subscribe(
        async response => {
          if (response.success) {
            if (this.resultsFound(response.payload)) {
              this.router.navigate(['/search-results'], { state: response.payload })
            }
            else {
              await this.toastWrapper.presentErrorToast('There were no search results.');
            }
          }
        }
      );
  }

  resultsFound(results: SearchResults) {
    return results.exisitingCustomerResults.length > 0
      || results.existingEquipmentResults.length > 0
      || results.prospectCustomerResults.length > 0
      || results.quoteResults.length > 0
      || results.recommendationEquipmentResults.length > 0
      || results.recommendationResults.length > 0
      || results.surveyCustomerResults.length > 0
      || results.surveyEquipmentResults.length > 0;
  }

  async triggerGpsPopulation() {
    try {

      await this.loadingWrapper.presentLoading('');

      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,       // optional: fail if it takes too long
        maximumAge: 0         // optional: don't use cached location
      });
      let coordinates = <PostFieldFromGpsGet>{
        longitude: position.coords.longitude,
        latitude: position.coords.latitude
      };

      this.userService.getFieldsFromGps(coordinates)
        .pipe(finalize(async () => {
          await this.loadingWrapper.dismissLoading();
        }))
        .subscribe(
          async response => {
            if (response.success) {
              this.postSearch.city = response.payload.city;
              this.postSearch.street = response.payload.street;
              this.postSearch.street_number = response.payload.streetNumber;
            }
            else {
              await this.toastWrapper.presentErrorToast(response.message);
            }
          }
        );

    } catch (error) {
      await this.loadingWrapper.dismissLoading();
      this.toastWrapper.presentErrorToast('Cannot get your current location.');
    }

  }

  async triggerBarcodeScanner() {
    if (this.isMobilePlatform) {
      this.scanActive = true;

      // TODO: This now throws an error within the await and it cannot be caught via a try/catch.  This means the camera can try to open and fail, and the user gets stuck with the scan modal open
      const result = await CapacitorBarcodeScanner.scanBarcode({ hint: CapacitorBarcodeScannerTypeHint.ALL, cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK });
      if (result.ScanResult) {
        this.postSearch.serial_number = this.postSearch.serial_number = result.ScanResult;
      }
      this.scanActive = false;
    }
  }
}
